import { ChangeEvent, useState } from 'react';

import { DatePicker } from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { required, requiredAs } from '../../utils/fieldValidation';

export interface USEntryInformationFormData {
  firstEntryDate: string;
  entryVisaType: string;
  yearsInUS: string;
}

const yearsOptions = [
  { label: '- Select an option -', value: '' },
  { label: 'Less than 1 year', value: '<1' },
  { label: '1 year', value: '1' },
  { label: '2 years', value: '2' },
  { label: '3 years', value: '3' },
  { label: '4 years', value: '4' },
  { label: '5 years', value: '5' },
];

function USEntryInformationPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<USEntryInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'us-entry-information-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      {
        field: 'firstEntryDate',
        label: 'When did you first enter the United States?',
        validationRules: [requiredAs('First entry date is required')],
      },
      {
        field: 'entryVisaType',
        label: 'On what type of visa did you enter the United States?',
        validationRules: [requiredAs('Entry visa type is required')],
      },
      { field: 'yearsInUS', label: 'Years in the U.S. of the Past Five (5) Years', validationRules: [required] },
    ],
  });

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 80);

  const handleChange = (field: keyof USEntryInformationFormData, value: string) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handleEntryVisaTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 64);
    handleChange('entryVisaType', value);
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-first-entry-date`}>
        <DatePicker
          required
          data-testid={`${baseTestId}-first-entry-date-picker`}
          errorMessage={getFieldError('firstEntryDate')}
          fromDate={minDate}
          hint='Select the date you first entered the United States'
          id='firstEntryDate'
          label='When did you first enter the United States?'
          name='firstEntryDate'
          toDate={today}
          tooltipText='The date you first entered the United States. This may be different from your immigration date if you entered on a temporary visa before becoming a permanent resident or obtaining other immigration status.'
          value={data.firstEntryDate || ''}
          onBlur={() => handleBlur('firstEntryDate')}
          onDateChange={(date, formattedDate) => handleChange('firstEntryDate', formattedDate || '')}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-entry-visa-type`}>
        <TextField
          required
          errorMessage={getFieldError('entryVisaType')}
          hint='Enter the visa type (e.g., H-1B, F-1, B-2). Maximum 64 characters.'
          label='On what type of visa did you enter the United States?'
          maxLength={64}
          name='entryVisaType'
          tooltipText='The type of visa you used when you first entered the United States. Common examples include H-1B (work visa), F-1 (student visa), B-2 (tourist visa), or other visa categories. Enter the visa classification as shown on your visa document.'
          type='text'
          value={data.entryVisaType || ''}
          onBlur={() => handleBlur('entryVisaType')}
          onChange={handleEntryVisaTypeChange}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-years-in-us`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-years-in-us-dropdown`}
          errorMessage={getFieldError('yearsInUS')}
          hint='Select the number of years you have been in the U.S. in the past 5 years'
          label='Years in the U.S. of the Past Five (5) Years'
          name='yearsInUS'
          options={yearsOptions}
          tooltipText='The total number of years you have been physically present in the United States during the past five years. This information may be used for background check purposes.'
          value={data.yearsInUS || ''}
          onBlur={() => handleBlur('yearsInUS')}
          onChange={(value: string) => handleChange('yearsInUS', value)}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: U.S. Entry Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with U.S. Entry Details</h3>
          <p>
            Please provide details about when and how you first entered the United States. We will need these details to
            verify if you are eligible for employment.
          </p>

          <h4 className='ds-u-margin-top--3'>When did you first enter the United States?</h4>
          <p>
            Enter the date you first arrived in the United States. This could be before you became a permanent resident
            or obtained a different immigration status. For example, if you first arrived on a temporary visa, such as a
            tourist or student visa, include that date.
          </p>

          <h4 className='ds-u-margin-top--3'>On what type of visa did you enter the United States?</h4>
          <p>Enter the type of visa you used when you first entered the United States. Common visa types include:</p>
          <ul>
            <li>
              <strong>H-1B:</strong> Specialty occupation work visa
            </li>
            <li>
              <strong>F-1:</strong> Student visa
            </li>
            <li>
              <strong>B-2:</strong> Tourist visa
            </li>
            <li>
              <strong>J-1:</strong> Exchange visitor visa
            </li>
            <li>
              <strong>L-1:</strong> Intracompany transferee visa
            </li>
            <li>
              <strong>Other:</strong> Any other visa classification
            </li>
          </ul>
          <p>Enter the visa classification as it appears on your visa document.</p>

          <h4 className='ds-u-margin-top--3'>Years in the U.S. of the Past Five (5) Years</h4>
          <p>
            Select the total number of full years you have been physically present in the United States over the past
            five years. Please do not round your time up to the next full year. This information may be used to run a
            background check.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default USEntryInformationPage;
